<template>
  <div>
    <div class="text-xl font-bold pb-2 text-center">
      {{ i18n.get_started }}
      <select
        class="inline w-auto text-black text-xl font-bold"
        :class="applicationSlug ? 'align-baseline' : 'align-middle'"
        v-model="applicationSlug"
        :size="applicationSlug ? 1 : Object.keys(applications).length"
      >
        <option
          v-for="(application, slug) in applications"
          :key="slug"
          :value="slug"
          v-text="application"
        />
      </select>
    </div>
    <ol
      v-if="application && application.slug == applicationSlug"
      class="list-decimal list-inside px-5"
    >
      <li
        v-if="
          application.ranchecker &&
          application.ranchecker.tools &&
          application.ranchecker.tools.length
        "
        class="py-4"
      >
        {{ application.ranchecker.download_title }}
        <div
          class="flex items-center"
          v-for="tool in sortedTools"
          :key="tool.download_url"
        >
          <ranch-button
            :endpoint="tool.download_url"
            :icon="[tool.os_icon == 'download' ? 'fa' : 'fab', tool.os_icon]"
            :label="application.name + ' · ' + tool.name"
            :action="hightlightTool(tool) ? 'action' : 'action-secondary'"
          />
          <p
            class="text-gray-700 text-xs"
            :class="hightlightTool(tool) > 1 ? 'font-bold text-orange-700' : ''"
            v-text="tool.last_update"
          />
        </div>
      </li>

      <li class="py-4">
        <span
          v-html="replaceMarkdownLink(application.ranchecker.install_title)"
        />
        <a
          v-if="application.ranchecker.youtube_id"
          :href="'https://youtu.be/' + application.ranchecker.youtube_id"
          :class="embed_youtube ? '' : 'hover:underline text-link'"
          @click.prevent="embed_youtube = !embed_youtube"
          >{{ application.ranchecker.video_title
          }}{{ embed_youtube ? ":" : "." }}</a
        >
        <div v-if="embed_youtube" class="-mx-5">
          <iframe
            class="w-full"
            width="800"
            height="450"
            :src="
              'https://www.youtube-nocookie.com/embed/' +
              application.ranchecker.youtube_id +
              '?rel=0&autoplay=1&modestbranding=1&widget_referrer=app_dashboard&start=5'
            "
            frameborder="0"
            allow="accelerometer; autoplay; encrypted-media; gyroscope; picture-in-picture"
            allowfullscreen
          ></iframe>
        </div>
      </li>

      <li v-if="application.releases && application.renderers" class="py-4">
        <form @submit.prevent class="inline">
          <label
            >{{ application.i18n.versions_title }}
            <div class="flex" v-if="application.renderers.length > 1">
              <input
                type="search"
                class="rounded-r-none"
                :placeholder="application.i18n.versions_placeholder"
                v-model.trim="search"
              />
              <button
                type="submit"
                class="rounded-r px-2 text-sm bg-gray-400 text-gray-900"
              >
                <font-awesome-icon icon="search" />
              </button>
            </div>
          </label>
        </form>
        <div class="my-4 -mx-5 overflow-auto">
          <table
            class="text-center mx-auto whitespace-no-wrap text-gray-900 border-t border-b text-sm"
          >
            <thead>
              <tr class="bg-white">
                <th
                  class="px-2 py-1 sticky left-0 text-center bg-white font-normal"
                >
                  {{ application.i18n.renderer_title }}
                </th>
                <th
                  class="px-2 left-0 font-normal"
                  v-for="release in application.releases"
                  :key="release.slug"
                >
                  {{ application.name }}<br /><strong>{{
                    release.version
                  }}</strong>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(renderer, i) in renderers"
                :key="renderer.slug"
                :class="i % 2 ? 'bg-white' : 'bg-gray-200'"
              >
                <th
                  class="px-2 py-1 sticky left-0 text-left"
                  :class="i % 2 ? 'bg-white' : 'bg-gray-200'"
                >
                  {{ renderer.name }}
                </th>
                <td
                  class="px-2 left-0"
                  v-for="release in application.releases"
                  :key="release.slug"
                  :class="{
                    'text-gray-700 text-xs': !renderer.releases[release.slug],
                    'bg-menu-hover':
                      renderer.releases[release.slug] &&
                      renderer.releases[release.slug].updated &&
                      application.latestProject,
                  }"
                  :title="
                    renderer.releases[release.slug] ? '' : 'Not available'
                  "
                >
                  <template v-if="!renderer.releases[release.slug]">
                    n/a
                  </template>
                  <template v-else-if="renderer.releases[release.slug].version">
                    {{ renderer.releases[release.slug].version }}
                  </template>
                  <template v-else>✔</template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          class="my-4 -mx-5 overflow-auto"
          v-if="application.addons && application.addons.length"
        >
          <table
            class="text-center mx-auto whitespace-no-wrap text-gray-900 border-t border-b text-sm"
          >
            <thead>
              <tr class="bg-white">
                <th
                  class="px-2 py-1 sticky left-0 text-center bg-white font-normal"
                >
                  {{ application.i18n.plugin_title }}
                </th>
                <th
                  class="px-2 left-0 font-normal"
                  v-for="release in application.releases"
                  :key="release.slug"
                >
                  {{ application.name }}<br /><strong>{{
                    release.version
                  }}</strong>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="(addon, i) in addons"
                :key="addon.id"
                :class="i % 2 ? 'bg-white' : 'bg-gray-200'"
              >
                <th
                  class="px-2 py-1 sticky left-0 text-left"
                  :class="i % 2 ? 'bg-white' : 'bg-gray-200'"
                >
                  {{ addon.name }}
                </th>
                <td
                  class="px-2 left-0"
                  v-for="release in application.releases"
                  :key="release.slug"
                  :class="{
                    'text-gray-700 text-xs': !addon.releases[release.slug],
                    'bg-menu-hover':
                      addon.releases[release.slug] &&
                      addon.releases[release.slug].updated &&
                      application.latestProject,
                  }"
                  :title="addon.releases[release.slug] ? '' : 'Not available'"
                >
                  <template v-if="!addon.releases[release.slug]">
                    n/a
                  </template>
                  <template
                    v-else-if="
                      addon.releases[release.slug].version &&
                      addon.releases[release.slug].version != '-'
                    "
                  >
                    {{ addon.releases[release.slug].version }}
                  </template>
                  <template v-else>✔</template>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </li>
    </ol>
    <div
      class="flex justify-center py-2"
      v-else-if="isLoading && applicationSlug"
    >
      <loading textColor="text-gray-700" />
    </div>
    <div
      v-else-if="error"
      class="border-red-200 bg-red-100 text-red-800 py-2 px-5"
    >
      {{ error }}
    </div>
    <div
      class="pb-3 px-5 flex justify-between items-center border-b border-gray-100"
    >
      <div class="pr-5" v-html="i18n.comment"></div>
      <div class="flex justify-center">
        <ranch-button
          v-if="search"
          :label="i18n.comment_button"
          icon="envelope-open-text"
          :route="supportLocation"
          action="action"
          class="w-56"
        />
        <ranch-button
          v-else
          :label="i18n.comment_button"
          icon="envelope-open-text"
          :route="supportLocation"
          action="action-secondary"
          class="w-56"
        />
      </div>
    </div>
  </div>
</template>

<script>
import useSWRV from "@/store/swrv";
import { useStore, computed } from "@/store/composition";
import { ref } from "@vue/composition-api";

export default {
  props: ["i18n"],
  setup() {
    const store = useStore();

    const applicationSlug = ref(store.getters["app/userApplicationSlug"]);
    const search = ref("");

    const { data: application, error, isLoading, reload, isLoaded } = useSWRV(
      () =>
        applicationSlug.value &&
        "/api/app/application/" + applicationSlug.value + "/get-started"
    );

    const renderers = computed(() => {
      if (!application.value || !application.value.renderers) {
        return null;
      }
      if (!search.value) {
        return application.value.renderers;
      }
      const s = search.value.toLowerCase();
      return application.value.renderers.filter((r) => {
        return r.name.toLowerCase().includes(s);
      });
    });
    const addons = computed(() => {
      if (!application.value || !application.value.addons) {
        return null;
      }
      if (!search.value) {
        return application.value.addons;
      }
      const s = search.value.toLowerCase();
      return application.value.addons.filter((a) => {
        return a.name.toLowerCase().includes(s);
      });
    });

    return {
      application,
      renderers,
      addons,

      isLoading,
      isLoaded,
      reload,
      error,

      applications: store.state.app.applications,

      applicationSlug,
      search,

      embed_youtube: ref(false),
    };
  },
  computed: {
    supportLocation() {
      return {
        query: Object.assign({}, this.$route.query, {
          supportModal: "project",
          usual_software: this.applicationSlug,
        }),
      };
    },
    sortedTools() {
      if (this.application.ranchecker.tools.length <= 1) {
        return this.application.ranchecker.tools;
      }
      return this.application.ranchecker.tools.slice().sort((a) => {
        if (a.os && a.os != this.userPlatform) {
          return 1;
        }
        return -1;
      });
    },
    userPlatform() {
      return navigator.platform.indexOf("Mac") > -1 ? "mac" : "win";
    },
  },
  methods: {
    hightlightTool(tool) {
      if (tool.os && tool.os != this.userPlatform) {
        return 0;
      }
      if (!this.application.latestProject) {
        // first project with this software
        return 1;
      }
      if (
        tool.days_since_release <
        this.application.latestProject.days_since_created_at + 15
      ) {
        // update more recent than latest project
        return 2;
      }

      // likely up to date
      return 0;
    },
    replaceMarkdownLink(text) {
      let el = document.createElement("div");
      el.textContent = text;
      return el.innerHTML.replace(
        /\[([^\]]+)\]\(([^)]+)\)/g,
        '<a href="$2" class="hover:underline text-link" target="_blank">$1</a>'
      );
    },
  },
};
</script>
